import { useEffect, useState } from 'react'

import { getPhysicianCountries } from '@/api/actions'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import { CountrySelectComponent } from '@/types/components'
import { Country, State } from '@/types/country'

import { Select } from '../Select'

const sortByTranslation = (a: { textKey: string }, z: { textKey: string }) => i18n.t(a.textKey).localeCompare(i18n.t(z.textKey))

enum DATA_KEYS {
  COUNTRY = 'country',
  STATE = 'state'
}

interface SelectedValues {
  country: string | undefined
  state?: string | undefined
}

type CountrySelectProps = Omit<CountrySelectComponent, 'componentType'> & {
  tabIndex: number
}

export const CountrySelect = ({
  id,
  countryTextKey,
  countryInputPlaceholder,
  stateTextKey,
  stateInputPlaceholder,
  required,
  tabIndex
}: CountrySelectProps) => {
  const { getUserDataById, addData, clearDataById } = useUserForm()
  const { clearFormError, getFieldError } = useFormErrors()
  const [options, setOptions] = useState<Country[]>([])
  const [selected, setSelected] = useState<SelectedValues>(() => {
    const country = getUserDataById('country') as Country
    const state = getUserDataById('state') as State

    return { country: country?.code, state: state?.code }
  })

  const getCountryStates = (countryCode?: string) => {
    if (!countryCode) return []

    const country = options.find(({ code }) => code === countryCode)
    return country?.states ?? []
  }

  useEffect(() => {
    if (options.length === 0) return

    const { country, state } = selected

    if (!country) return

    const countryObj = options.find(({ code }) => code === country)
    if (!countryObj) throw new Error('Country not found')

    const countryStates = getCountryStates(country)

    if (countryStates.length === 0) {
      // Add country data to userForm
      return addData(DATA_KEYS.COUNTRY, countryObj)
    }

    // If country has states, check if state is selected
    if (!state) {
      // If country has states but no state is selected, clear country data in order to trigger an error
      return clearDataById(DATA_KEYS.COUNTRY)
    }

    const stateObj = countryStates.find(({ code }) => code === state)
    if (!stateObj) throw new Error('State not found')

    // Add country data to userForm
    addData(DATA_KEYS.COUNTRY, countryObj)

    // Add state data to userForm
    addData(DATA_KEYS.STATE, stateObj)
  }, [selected])

  useEffect(() => {
    getPhysicianCountries()
      .then(setOptions)
      .then(() => {
        if (!selected.country) setSelected({ country: 'us' }) // Default country
      })
  }, [])

  const availableStates = getCountryStates(selected.country)
  const error = getFieldError(id)

  return (
    <div className="flex flex-col gap-6">
      {/* Countries input */}
      <Select
        id={`${id}-country`}
        tabIndex={tabIndex}
        textKey={countryTextKey}
        value={selected.country}
        options={options
          .map(({ code }) => ({
            id: code,
            textKey: `country.${code}`,
            value: code
          }))
          .sort(sortByTranslation)}
        onChange={({ value }) => {
          clearFormError(id)
          setSelected({
            state: undefined,
            country: value
          })
        }}
        required={required}
        placeholder={countryInputPlaceholder}
        error={!selected.country ? error : undefined}
      />

      {/* States input */}
      <Select
        id={`${id}-state`}
        tabIndex={tabIndex + 1}
        textKey={stateTextKey}
        disabled={availableStates.length === 0}
        value={selected.state}
        options={availableStates
          .map(({ code }) => ({
            id: code,
            textKey: `country.${selected.country}.area.${code}`,
            value: code
          }))
          .sort(sortByTranslation)}
        onChange={({ value }) => {
          clearFormError(id)
          setSelected((prev) => ({
            ...prev,
            state: value
          }))
        }}
        placeholder={stateInputPlaceholder}
        error={!selected.state ? error : undefined}
        required={availableStates.length > 0}
      />
    </div>
  )
}
